import { MapHelper } from 'Plugins/FloorPlan/Helpers/MapHelper';
import { useCurrentProject } from '../../../../Configs/CurrentProjectContext';
import { useMe } from '../../../../Configs/PolStore';
import { Logger } from '../../../../Errors/Logger';
import { createCustomErrorHandler, wrapError } from '../../../../Errors/PolErrorHandler';
import { ConfigurationUtils, ToConfigurationReponseParameters } from '../../../../Helpers/ConfigurationUtils';
import { QuestionsHelper } from '../../../../Helpers/QuestionsHelper';
import { PrixContextDebugMap, QuotationHelper } from '../../../../Helpers/QuotationHelper';
import { RoomHeatingTypeErrorHelper } from '../../../../Helpers/RoomHeatingTypeErrorHelper';
import { SetConfigurationDto, SetConfigurationReponseDto } from '../../../../Models/Projects/Configuration';
import { ComputeQuotationsResponse, QuotationReplyBaseDto } from '../../../../Models/Quotations/Quotations';
import { TauxTvaDto } from '../../../../Models/VersionParametrages/VersionParametrage';
import { MapContextState, useMapProvider } from '../../../../Plugins/FloorPlan/Context/MapContext';
import { MapExportContextValue, useMapExport } from '../../../../Plugins/FloorPlan/Context/MapExportContext';
import { MapExportHelper } from '../../../../Plugins/FloorPlan/Helpers/MapExport/MapExportHelper';
import { IMapItem } from '../../../../Plugins/FloorPlan/Models/IMapItem';
import { IOpening } from '../../../../Plugins/FloorPlan/Models/IOpening';
import { IRoom } from '../../../../Plugins/FloorPlan/Models/IRoom';
import { IRoomItem } from '../../../../Plugins/FloorPlan/Models/IRoomItem';
import { GTM, GtmSaveType } from '../../../../Plugins/GTM/GTM';
import { QuotationsApi } from '../../../../Services/Api/QuotationsApi';
import { AuthHelper } from '../../../../Services/Authentication/AuthHelper';
import { MeState } from '../../../../Services/Me/MeReducer';
import { useQuoteContext } from '../QuotePageController';
import { QuoteState } from '../QuoteState';
import { useHabitationContextController } from './HabitationContextController';
import { useQuoteSaveController } from './QuoteSaveController';
import { useDialogs } from 'Web/Services/useDialogs';
import { I18n } from 'Locales/I18nService';
import { StoreHelper } from 'Helpers';

type OnRefreshQuotationParams = {
    nextAction?: VoidFunction;
    forceSave?: boolean;
    setRefreshing?: (loading: boolean) => void;
    withPrestation?: boolean;
};
type RefreshQuotationPricesParams = {
    nextAction?: VoidFunction;
    saveConfig?: () => Promise<boolean>;
    setRefreshing?: (loading: boolean) => void;
    withPrestation?: boolean;
};

export const useQuoteProvider = () => {
    const dialog = useDialogs();
    const map = useMapProvider();
    const quoteContext = useQuoteContext();
    const habitation = useHabitationContextController(quoteContext.getState().stepHabitation?.questions);
    const exportImageMap = useMapExport();
    const currentProject = useCurrentProject();
    const me: MeState = useMe();

    const getTauxTva = (): TauxTvaDto => quoteContext.getState().versionData?.taux_tva!;

    const getGeneralContext = () => {
        const { stepHabitation } = quoteContext.getState();
        return QuestionsHelper.getLastShownQuestion(stepHabitation?.questions).lastShownQuestion?.context || {};
    };

    const getConfiguration = async (): Promise<SetConfigurationDto> => {
        return QuoteLoadUtils.getConfiguration({
            mapState: map.getState(),
            quoteState: quoteContext.getState(),
        });
    };

    const getGetQuotationDto = async (with_prestation?: boolean) => {
        return QuoteLoadUtils.getGetQuotationDto({
            exportImageMap,
            mapState: map.getState(),
            quoteState: quoteContext.getState(),
            with_prestation,
        });
    };

    const handleQuotationError = (error: any): Promise<boolean> => {
        quoteContext.updateQuote({ loadingQuotation: false });
        if (RoomHeatingTypeErrorHelper.isErrorFromRoomHeatingTypeHelper(error)) {
            dialog.showDeadEndError({
                title: I18n.get('ERROR_ROOM_HEATING_TYPE_MISSING_TITLE'),
                body: I18n.get('ERROR_ROOM_HEATING_TYPE_MISSING_CONTENT'),
                labelChoice: I18n.get('COMMON_CONTINUE'),
                onClickChoice: ({ closeDialog }) => closeDialog()
            });
            return Promise.resolve(true);
        } else {
            //this.handleDeadEndError();
            return createCustomErrorHandler(AuthHelper.isAuthenticatedAsStoreOrBOUser())(error);
        }
    };

    const refreshQuotationPrices = ({
        nextAction,
        saveConfig,
        setRefreshing,
        withPrestation,
    }: RefreshQuotationPricesParams): Promise<QuotationReplyBaseDto> => {
        if (quoteContext.getState().loadingQuotation) return Promise.resolve({} as QuotationReplyBaseDto);
        return wrapError<QuotationReplyBaseDto>(
            async () => {
                const getQuote = async (): Promise<{
                    quote: QuotationReplyBaseDto;
                    quotePrixContextDebugMap: PrixContextDebugMap;
                }> => {
                    const currentProjectState = currentProject.getState();
                    const { bodyRequest, quotePrixContextDebugMap } = await getGetQuotationDto(withPrestation);
                    if (currentProjectState.projet) {
                        const response = await QuotationsApi.createQuotation(
                            currentProjectState.projet.id,
                            bodyRequest
                        );
                        return { quote: response.data, quotePrixContextDebugMap };
                    } else {
                        const response: ComputeQuotationsResponse = await QuotationsApi.computeQuotation(bodyRequest);
                        return { quote: response.data, quotePrixContextDebugMap };
                    }
                };

                const loadQuote = async () => {
                    quoteContext.updateQuote({ loadingQuotation: true });
                    const { quote, quotePrixContextDebugMap } = await getQuote();
                    GTM.pushEstimationEvent({
                        projetId: currentProject.getState().projet?.id,
                        totalPrice: quote.detail_total_price.total_price_ttc,
                        products: GTM.getGTMProducts(map.getState().rooms ?? []),
                        user_mail: me?.data?.customer_properties?.email ?? '',
                    });

                    currentProject.update({ isDirty: false });
                    quoteContext.updateQuote({ loadingQuotation: false, quote, quotePrixContextDebugMap });
                    return quote;
                };

                setRefreshing?.(true);
                if (saveConfig) {
                    await saveConfig();
                }
                const quoteResponse = await loadQuote();
                setRefreshing?.(false);
                nextAction?.();
                return quoteResponse;
            },
            {
                errorHandler: (error) => {
                    setRefreshing?.(false);
                    return handleQuotationError(error);
                },
            }
        );
    };

    return {
        loadingQuotation: quoteContext.getState().loadingQuotation,
        getTauxTva,
        habitation,
        getGeneralContext,
        getConfiguration,
        refreshQuotationPrices,
        getState: quoteContext.getState,
        updateQuote: quoteContext.updateQuote,
        update: quoteContext.update,
        openFinDeParcours: quoteContext.openFinDeParcours,
        map,
    };
};

type GetSetConfigurationDtoParams = { mapState: Partial<MapContextState>; quoteState: Partial<QuoteState> };
type GetGetQuotationDtoParams = GetSetConfigurationDtoParams & {
    exportImageMap: MapExportContextValue;
    with_prestation?: boolean;
};

export class QuoteLoadUtils {
    public static getConfiguration = ({ quoteState, mapState }: GetSetConfigurationDtoParams): SetConfigurationDto => {
        const { versionId, stepHabitation, stepServices, stepSynthese } = quoteState;
        const { projetStoreLead, projetStoreDelivery } = StoreHelper.getProjectStores(quoteState);

        const toConfigurationResponses = <T extends IMapItem>(
            items: Array<T> = [],
            getParams: (item: T) => Partial<ToConfigurationReponseParameters>
        ): Array<SetConfigurationReponseDto> => {
            let responses: Array<SetConfigurationReponseDto> = [];
            items.forEach((item) => {
                const { questionsTva, questionsProduit, questionsPose, questionsSupport, questionsFinition, questionsPreparation } = item;
                const params = getParams(item);
                //* ROOM
                responses.push(...ConfigurationUtils.toConfigurationReponses(questionsTva?.questions, params));
                responses.push(...ConfigurationUtils.toConfigurationReponses(questionsProduit?.questions, params));
                responses.push(...ConfigurationUtils.toConfigurationReponses(questionsPose?.questions, params));
                responses.push(...ConfigurationUtils.toConfigurationReponses(questionsSupport?.questions, params));
                responses.push(...ConfigurationUtils.toConfigurationReponses(questionsPreparation?.questions, params));
                responses.push(...ConfigurationUtils.toConfigurationReponses(questionsFinition?.questions, params));
            });
            return responses;
        };
        const { roomItems, openings } = MapHelper.getRoomAllElements(mapState.rooms);
        const responseList: Array<SetConfigurationReponseDto> = [
            ...ConfigurationUtils.toConfigurationReponses(stepHabitation?.questions),
            ...toConfigurationResponses<IRoom>(mapState.rooms, (item) => ({ item, room_id: item.roomId })),
            ...toConfigurationResponses<IOpening>(openings, (item) => ({
                opening_id: item.openingId,
                room_id: item.roomId,
            })),
            ...toConfigurationResponses<IRoomItem>(roomItems, (item) => ({
                room_element_id: item.roomItemId,
                room_id: item.roomId,
            })),
            ...ConfigurationUtils.toConfigurationReponses(stepServices?.questions),
            ...ConfigurationUtils.toConfigurationReponses(stepSynthese?.questions),
        ];

        const planJson = MapExportHelper.toApiJson(mapState);

        Logger.isLogEnabled() && console.groupCollapsed('configuration');
        Logger.log('planJson', JSON.parse(planJson));
        Logger.log('responseList', responseList);
        const bodyRequest: SetConfigurationDto = {
            version_parametrage_id: versionId!,
            floor_plan_json: planJson,
            reponse_list: responseList,
            total_surface: mapState.global_surface_area!,
            opening_count: openings?.length ?? 0,
            room_count: mapState.rooms?.length ?? 0,
            room_element_count: roomItems?.length ?? 0,
            store_id_lead: projetStoreLead?.store_id!,
            store_id_delivery: projetStoreDelivery?.store_id!,
        };
        Logger.log('bodyRequest', bodyRequest);
        Logger.isLogEnabled() && console.groupEnd();
        return bodyRequest;
    };

    public static getGetQuotationDto = async ({
        quoteState,
        mapState,
        exportImageMap,
        with_prestation,
    }: GetGetQuotationDtoParams) => {
        const bodyRequest = await QuotationHelper.getGetQuotationDto({
            map: mapState,
            quoteState,
            exportImageMap,
            with_prestation,
        });
        return bodyRequest;
    };
}

export const useRefreshQuote = (props: { nextAction?: VoidFunction }) => {
    const currentProject = useCurrentProject();
    const { refreshQuotationPrices: refresh } = useQuoteProvider();
    const { requestCreateProject, updateProject } = useQuoteSaveController();

    const saveConfig = async (): Promise<boolean> => {
        return new Promise(async (resolve, reject) => {
            const currentProjectState = currentProject.getState();
            if (currentProjectState.projet) {
                const { id, intitule } = currentProjectState.projet;
                await updateProject({
                    projectId: id,
                    projectName: intitule,
                    withPostMessage: true,
                    saveType: GtmSaveType.DevisEstimatif,
                });
                resolve(true);
            } else {
                resolve(false);
            }
        });
    };

    const onRefreshQuotationPrices = ({
        forceSave,
        setRefreshing,
        nextAction,
        withPrestation,
    }: OnRefreshQuotationParams) => {
        const currentProjectState = currentProject.getState();
        if (currentProjectState?.projet && !currentProjectState.is_update_allowed) {
            requestCreateProject({ callback: () => refresh({ nextAction, setRefreshing, withPrestation }) });
        } else {
            refresh({
                nextAction: nextAction || props.nextAction,
                saveConfig: forceSave ? saveConfig : undefined,
                setRefreshing,
                withPrestation,
            });
        }
    };

    return { onRefreshQuotationPrices };
};
